<script setup lang="ts">
/**
 * Design System Input Component
 *
 * @example
 * <DSInput v-model="email" type="email" label="Email" placeholder="your@email.com" />
 */

interface Props {
  modelValue?: string | number
  type?: 'text' | 'email' | 'password' | 'number' | 'tel' | 'url'
  label?: string
  placeholder?: string
  error?: string
  helpText?: string
  required?: boolean
  disabled?: boolean
  icon?: string
  iconPosition?: 'left' | 'right'
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  iconPosition: 'left'
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number]
}>()

const inputClasses = computed(() => {
  const classes = [
    'block',
    'w-full',
    'px-3',
    'py-2',
    'border',
    'rounded-md',
    'shadow-sm',
    'placeholder-gray-400',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-0',
    'transition-all',
    'duration-200'
  ]

  if (props.error) {
    classes.push('border-red-300', 'focus:ring-red-500', 'focus:border-red-500')
  } else {
    classes.push('border-gray-300', 'focus:ring-brand-500', 'focus:border-brand-500')
  }

  if (props.disabled) {
    classes.push('bg-gray-100', 'cursor-not-allowed')
  }

  if (props.icon) {
    if (props.iconPosition === 'left') {
      classes.push('pl-10')
    } else {
      classes.push('pr-10')
    }
  }

  return classes.join(' ')
})

const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  emit('update:modelValue', target.value)
}
</script>

<template>
  <div class="w-full">
    <!-- Label -->
    <label v-if="label" class="block text-sm font-medium text-gray-700 mb-2">
      {{ label }}
      <span v-if="required" class="text-red-500">*</span>
    </label>

    <!-- Input Wrapper -->
    <div class="relative">
      <!-- Left Icon -->
      <div
        v-if="icon && iconPosition === 'left'"
        class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
      >
        <UIcon :name="icon" class="w-5 h-5 text-gray-400" />
      </div>

      <!-- Input -->
      <input
        :value="modelValue"
        :type="type"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        :class="inputClasses"
        @input="handleInput"
      >

      <!-- Right Icon -->
      <div
        v-if="icon && iconPosition === 'right'"
        class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
      >
        <UIcon :name="icon" class="w-5 h-5 text-gray-400" />
      </div>
    </div>

    <!-- Error Message -->
    <p v-if="error" class="mt-2 text-sm text-red-600">
      {{ error }}
    </p>

    <!-- Help Text -->
    <p v-else-if="helpText" class="mt-2 text-sm text-gray-500">
      {{ helpText }}
    </p>
  </div>
</template>
